import React, { useState, useRef, useCallback } from 'react';
import QrReader from 'modern-react-qr-reader';
import axios from 'axios';
import { baseUrl } from '../../redux/actions/baseUrl';
import { useNavigate } from 'react-router-dom';
import { toast } from "../../utils/toast";
import { useDispatch, useSelector } from 'react-redux';
import { loginUserAction } from '../../redux/actions/userActions';
import Loading from '../Loading';
import { useEffect } from 'react';
import axiosInstance from '../../axiosInstance';
import { getHospitalId } from '../../utils';

const Scanner3 = ({ setStart, start, onScanResult, type }) => {
    const [facingMode, setFacingMode] = useState('environment');
    const [result, setResult] = useState('No result');
    const [loading, setLoading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [cameraReady, setCameraReady] = useState(false);
    const lastScanRef = useRef(null);
    const scanTimeoutRef = useRef(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();


    const verify_qr = useCallback(async (data, type) => {
        if (isProcessing) return false;

        try {
            setIsProcessing(true);
            let hospital = await getHospitalId(data);
            if (!hospital) {
                localStorage.removeItem('hospital');
                setIsProcessing(false);
                return false;
            }

            const res = await axiosInstance.get(`/auth/check-barcode?qr_code=${encodeURIComponent(data)}&type=${encodeURIComponent(type)}`);

            if (res.data?.success) {
                let patient = res.data?.data?.patient;
                localStorage.setItem('temp_file_number', patient.system_id);
                dispatch(loginUserAction(patient.system_id, patient.firstname, patient.id, patient.qrcode || patient.qr_code));
                return true;
            }
            return false;
        } catch (err) {
            setLoading(false);
            setIsProcessing(false);
            localStorage.removeItem('hospital');
            toast.error('Please scan your QR Code again');
            console.log('err', err);
            return false;
        }
    }, [dispatch, isProcessing]);


    const handleScan = useCallback(async (data) => {
        console.log('🔍 Scan detected:', data);

        if (!data || isProcessing) {
            console.log('⏭️ Skipping scan - no data or already processing');
            return;
        }

        // Prevent duplicate scans
        setIsProcessing(true);

        try {
            console.log('📱 Processing QR code:', data);

            // Verify it's a valid QR code format
            if (typeof data === 'string' && data.trim()) {
                console.log('✅ Valid QR code detected:', data);
                const trimmed_data = data.trim();
                setResult(trimmed_data);

                // Handle different scan types
                if (type === 'login') {
                    try {
                        setLoading(true);
                        setStart(false);
                        toast.warning('Please wait while we verify your QR Code...', {
                            autoClose: 5000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                        });
                        await verify_qr(trimmed_data, 'login');
                    } catch (err) {
                        localStorage.removeItem('hospital');
                        setLoading(false);
                        toast.info('Please scan your QR Code again');
                        console.log('err', err);
                    }
                } else if (type === 'register') {
                    try {
                        setLoading(true);
                        setStart(false);
                        
                        if (onScanResult) {
                            onScanResult(trimmed_data);
                        }
                        
                        // await verify_qr(trimmed_data, 'register');
                        return;
                    } catch (err) {
                        setLoading(false);
                        localStorage.removeItem('hospital');
                        toast.info('Please scan your QR Code again');
                        setStart(false);
                        console.log('err', err);
                        return;
                    }
                } else {
                    // Default behavior for other types
                    console.log('window');
                    setStart(false);
                    
                    if (onScanResult) {
                        onScanResult(trimmed_data);
                    }
                    
                    toast.success('QR Code scanned successfully!');
                }
            } else {
                console.log('❌ Invalid QR code format');
                toast.error('Invalid QR code format');
            }
        } catch (error) {
            console.error('❌ Error processing scan:', error);
            toast.error('Error processing QR code');
            setLoading(false);
        } finally {
            // Reset processing state after a delay
            setTimeout(() => {
                setIsProcessing(false);
            }, 1000);
        }
    }, [isProcessing, onScanResult, setStart, type, verify_qr, setLoading]);


    const handleLoad = useCallback(() => {
        console.log('QR Reader loaded and ready');
        setCameraReady(true);
    }, []);

    const handleError = useCallback((error) => {
        console.log('Scanner error:', error);
        console.log('Error details:', {
            name: error?.name,
            message: error?.message,
            stack: error?.stack
        });

        if (error?.name === 'NotAllowedError') {
            toast.error('Camera access denied. Please allow camera permissions.');
        } else if (error?.name === 'NotFoundError') {
            toast.error('No camera found. Please check your device.');
        } else if (error?.name === 'NotReadableError') {
            toast.error('Camera is already in use by another application.');
        } else if (error?.name === 'OverconstrainedError') {
            console.log('Camera constraints not supported, trying fallback...');
        }

    }, []);

    const previewStyle = {
        height: "100%",
        width: "100%",
        borderRadius: '12px',
        overflow: 'hidden',
    };

    const handleCameraChange = (e) => {
        const newFacingMode = e.target.value;
        console.log('Changing camera to:', newFacingMode);
        setFacingMode(newFacingMode);
        setCameraReady(false);
    };

    useEffect(() => {
        // Cleanup function
        return () => {
            if (scanTimeoutRef.current) {
                clearTimeout(scanTimeoutRef.current);
            }
            setIsProcessing(false);
            setLoading(false);
        };
    }, []);

    useEffect(() => {
        if (!start) {
            setIsProcessing(false);
            lastScanRef.current = null;
            setCameraReady(false);
        } else {
            console.log('Scanner started, waiting for camera initialization...');
        }
    }, [start]);


    // Monitor video element loading
    useEffect(() => {
        if (start) {
            const checkVideoElement = () => {
                const videoElement = document.querySelector('video');
                if (videoElement) {
                    videoElement.addEventListener('loadedmetadata', () => {
                        console.log('Video metadata loaded');
                    });

                    videoElement.addEventListener('canplay', () => {
                        console.log('Video can start playing');
                    });
                } else {
                    console.log('Video element not found');
                }
            };

            // Check after a short delay to allow QrReader to render
            const timeout = setTimeout(checkVideoElement, 1000);
            return () => clearTimeout(timeout);
        }
    }, [start]);

    return (
        <>
            {loading ? (
                <Loading fullScreen={true} message="Scanning..." variant="pulse" />
            ) : (
                    <div className="flex justify-center items-center flex-col space-y-4 w-full">
                    {/* Camera Selection */}
                    <div className="w-full max-w-sm">
                        <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
                            Camera Selection
                        </label>
                        <select
                            className="w-full p-3 rounded-xl border-2 border-gray-200 focus:border-primary100 focus:outline-none bg-white text-center font-medium transition-colors duration-200"
                            onChange={handleCameraChange}
                            value={facingMode}
                            disabled={isProcessing}
                        >
                            <option value="environment">📷 Rear Camera </option>
                            <option value="user">🤳 Front Camera</option>
                        </select>
                    </div>

                    {/* Scanner Container */}
                        <div className="relative bg-transparent w-full">
                            <div className="rounded-2xl shadow-lg p-1 border-2 border-gray-100 w-full bg-transparent">
                            {start ? (
                                    <>
                                        <QrReader
                                            delay={300}
                                            facingMode={facingMode}
                                            onError={handleError}
                                            onScan={handleScan}
                                            onLoad={handleLoad}
                                            style={previewStyle}
                                        />

                                        {/* Simple Scanning Frame Overlay */}
                                        <div className="absolute inset-0 pointer-events-none z-10">
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <div className="relative w-48 h-48">
                                                    {/* Corner indicators */}
                                                    <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-green-400 rounded-tl-lg"></div>
                                                    <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-green-400 rounded-tr-lg"></div>
                                                    <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-green-400 rounded-bl-lg"></div>
                                                    <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-green-400 rounded-br-lg"></div>

                                                    {/* Scanning line animation */}
                                                    <div className="absolute inset-0 overflow-hidden">
                                                        <div className="absolute w-full h-1 bg-green-400 opacity-75 animate-pulse top-1/2 transform -translate-y-1/2"></div>
                                                    </div>

                                                    {/* Center text */}
                                                    <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
                                                        <div className="bg-black bg-opacity-70 text-white text-xs px-3 py-1 rounded-full whitespace-nowrap">
                                                            Position QR Code Here
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    {!cameraReady && (
                                        <div className="absolute inset-0 bg-gray-900 bg-opacity-95 rounded-xl flex items-center justify-center z-20">
                                            <div className="text-center text-white">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                                                <p className="text-sm font-medium">Initializing Camera...</p>
                                                <p className="text-xs text-gray-300 mt-1">Please allow camera access</p>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div
                                    style={previewStyle}
                                    className="bg-gray-100 rounded-xl flex items-center justify-center"
                                >
                                    <div className="text-center">
                                        <div className="text-gray-400 text-4xl mb-2">📷</div>
                                        <p className="text-gray-500 text-sm">Scanner Stopped</p>
                                        <p className="text-gray-400 text-xs mt-1">Click "Start Scanner" to begin</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Scanning Overlay */}
                        {isProcessing && (
                            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-2xl flex items-center justify-center">
                                <div className="bg-white rounded-xl p-4 flex items-center space-x-3">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary100"></div>
                                    <span className="text-gray-700 font-medium">Processing...</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Instructions */}
                    <div className="text-center max-w-sm">
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Position the QR code within the camera frame. The scanner will automatically detect and process the code.
                        </p>
                    </div>

                    {/* Status Indicator */}
                    <div className="flex items-center space-x-2 text-xs">
                        <div className={`w-2 h-2 rounded-full ${start ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
                        <span className="text-gray-600">
                            {start ? 'Scanner Active' : 'Scanner Stopped'}
                        </span>
                    </div>
                </div>
            )}
        </>
    );
};

export default Scanner3;
